// Button — primary, secondary, tertiary, danger variants × sm/md/lg sizes.
// Loading state swaps the children for a spinning Loader2 and disables the
// button. Hover, focus and disabled states all source from tokens; no raw
// hex/spacing in component code.
//
// Variant rationale (per spec §7.0 + tokens/colors.ts):
//   primary   — Teal solid; one per screen
//   secondary — Teal outline; supporting actions
//   tertiary  — text-only Teal; lightweight inline actions
//   danger    — Ember solid; destructive (deletes, undos with consequence)

import { Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-teal text-text-inverse border border-transparent hover:opacity-90 focus-visible:shadow-focus disabled:opacity-50",
  secondary:
    "bg-transparent text-teal border border-teal hover:bg-teal-tint focus-visible:shadow-focus disabled:opacity-50",
  tertiary:
    "bg-transparent text-teal border border-transparent hover:bg-teal-tint focus-visible:shadow-focus disabled:opacity-50",
  danger:
    "bg-ember text-text-inverse border border-transparent hover:opacity-90 focus-visible:shadow-focus disabled:opacity-50",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm gap-1.5",
  md: "px-4 py-2 text-base gap-2",
  lg: "px-6 py-3 text-md gap-2",
};

const SPINNER_SIZE: Record<ButtonSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled,
    type = "button",
    className,
    children,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      data-variant={variant}
      data-size={size}
      data-loading={loading ? "true" : "false"}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium transition-base ease-out",
        "outline-none disabled:cursor-not-allowed",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2
          size={SPINNER_SIZE[size]}
          className="animate-spin"
          aria-hidden="true"
          data-testid="button-spinner"
        />
      ) : (
        children
      )}
    </button>
  );
});
